import { useEffect, useState } from 'react';
import React from 'react';
import axios from 'axios';
import { Zap, TrendingUp, TrendingDown, Clock, ExternalLink, UserPlus, BarChart3 } from 'lucide-react';
import LeadSelector from '../components/LeadSelector';
import AttributionVisualizer from '../components/AttributionVisualizer';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showLeadSelector, setShowLeadSelector] = useState(null); // campaignId when selector is open
  const [selectedCampaignForAttribution, setSelectedCampaignForAttribution] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_id: '',
    daily_limit: 50,
    domains_assigned: [],
    targeting_grades: [],
    targeting_tech_stacks: []
  });

  const availableDomains = [
    'modernphoneservice.com',
    'phonesystemcompare.com',
    'smartcommschoice.com',
    'ucaascomparison.com',
    'phonesystemmatch.com',
    'rightphonesystem.com',
    'ucaasfinder.com',
    'businessphonefinder.com',
    'businessphoneguide.com',
    'chooseyourucaas.com'
  ];

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/campaigns`);
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE}/templates?status=approved`);
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/campaigns`, formData);
      setShowCreate(false);
      setFormData({
        name: '',
        description: '',
        template_id: '',
        daily_limit: 50,
        domains_assigned: [],
        targeting_grades: [],
        targeting_tech_stacks: []
      });
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Error creating campaign');
    }
  };

  const handleStart = async (campaignId) => {
    try {
      await axios.put(`${API_BASE}/campaigns/${campaignId}/start`);
      fetchCampaigns();
    } catch (error) {
      console.error('Error starting campaign:', error);
      alert('Error starting campaign');
    }
  };

  const handlePause = async (campaignId) => {
    try {
      await axios.put(`${API_BASE}/campaigns/${campaignId}/pause`);
      fetchCampaigns();
    } catch (error) {
      console.error('Error pausing campaign:', error);
      alert('Error pausing campaign');
    }
  };

  const handleImportToInstantly = async (campaignId) => {
    if (!confirm('This will create the campaign in Instantly, add leads, and start sending. Continue?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE}/campaigns/${campaignId}/import-to-instantly`);
      alert(`Campaign imported successfully! ${response.data.leads_added} leads added to Instantly.`);
      fetchCampaigns();
    } catch (error) {
      console.error('Error importing to Instantly:', error);
      alert(error.response?.data?.message || 'Error importing campaign to Instantly');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Campaigns</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create Campaign
        </button>
      </div>

      {showCreate && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Create New Campaign</h2>
          <form onSubmit={handleCreate}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily Limit</label>
                <input
                  type="number"
                  required
                  value={formData.daily_limit}
                  onChange={(e) => setFormData({ ...formData, daily_limit: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                <select
                  required
                  value={formData.template_id}
                  onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select template...</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Grades</label>
                <div className="flex gap-2">
                  {['A+', 'A', 'B'].map(grade => (
                    <label key={grade} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.targeting_grades.includes(grade)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, targeting_grades: [...formData.targeting_grades, grade] });
                          } else {
                            setFormData({ ...formData, targeting_grades: formData.targeting_grades.filter(g => g !== grade) });
                          }
                        }}
                        className="mr-1"
                      />
                      {grade}
                    </label>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Domains</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {availableDomains.map(domain => (
                    <label key={domain} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.domains_assigned.includes(domain)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, domains_assigned: [...formData.domains_assigned, domain] });
                          } else {
                            setFormData({ ...formData, domains_assigned: formData.domains_assigned.filter(d => d !== domain) });
                          }
                        }}
                        className="mr-1"
                      />
                      <span className="text-xs">{domain}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center p-8">Loading campaigns...</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No campaigns found. Create your first campaign to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <React.Fragment key={campaign.id}>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{campaign.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span className="font-medium">Status: <span className={`font-bold ${campaign.status === 'active' ? 'text-green-600' : campaign.status === 'paused' ? 'text-yellow-600' : 'text-gray-600'}`}>{campaign.status || 'draft'}</span></span>
                    {campaign.instantly_campaign_id && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Zap size={14} />
                        <span className="font-medium">Instantly: {campaign.instantly_status || 'syncing'}</span>
                      </span>
                    )}
                  </div>
                  
                  {/* Instantly Stats */}
                  {campaign.instantly_campaign_id && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Sends</div>
                        <div className="text-lg font-semibold">{campaign.sends_count || 0}</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          Opens
                          {campaign.open_rate > 0 && (
                            <TrendingUp size={12} className="text-green-600" />
                          )}
                        </div>
                        <div className="text-lg font-semibold">{campaign.opens_count || 0}</div>
                        {campaign.open_rate > 0 && (
                          <div className="text-xs text-green-600">{parseFloat(campaign.open_rate || 0).toFixed(1)}%</div>
                        )}
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          Clicks
                          {campaign.click_rate > 0 && (
                            <TrendingUp size={12} className="text-green-600" />
                          )}
                        </div>
                        <div className="text-lg font-semibold">{campaign.clicks_count || 0}</div>
                        {campaign.click_rate > 0 && (
                          <div className="text-xs text-green-600">{parseFloat(campaign.click_rate || 0).toFixed(1)}%</div>
                        )}
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Replies</div>
                        <div className="text-lg font-semibold">{campaign.replies_count || 0}</div>
                        {campaign.reply_rate > 0 && (
                          <div className="text-xs text-green-600">{parseFloat(campaign.reply_rate || 0).toFixed(1)}%</div>
                        )}
                      </div>
                      <div className="bg-red-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          Bounces
                          {campaign.bounce_rate > 0 && (
                            <TrendingDown size={12} className="text-red-600" />
                          )}
                        </div>
                        <div className="text-lg font-semibold">{campaign.bounces_count || 0}</div>
                        {campaign.bounce_rate > 0 && (
                          <div className="text-xs text-red-600">{parseFloat(campaign.bounce_rate || 0).toFixed(1)}%</div>
                        )}
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Conversions</div>
                        <div className="text-lg font-semibold">{campaign.conversions_count || 0}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Regular Stats (if not Instantly) */}
                  {!campaign.instantly_campaign_id && (
                    <div className="mt-4 flex items-center gap-4 text-sm">
                      <span>Sends: <strong>{campaign.sends_count || 0}</strong></span>
                      <span>Opens: <strong>{campaign.opens_count || 0}</strong></span>
                      <span>Clicks: <strong>{campaign.clicks_count || 0}</strong></span>
                      <span>Conversions: <strong>{campaign.conversions_count || 0}</strong></span>
                    </div>
                  )}
                  
                  {campaign.instantly_last_synced && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                      <Clock size={12} />
                      <span>Last synced: {new Date(campaign.instantly_last_synced).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {!campaign.instantly_campaign_id && (
                    <button
                      onClick={() => handleImportToInstantly(campaign.id)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Zap size={16} />
                      Import to Instantly
                    </button>
                  )}
                  {campaign.instantly_campaign_id && (
                    <a
                      href={`https://app.instantly.ai/app/campaigns/${campaign.instantly_campaign_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                    >
                      <ExternalLink size={16} />
                      View in Instantly
                    </a>
                  )}
                  {campaign.instantly_campaign_id && (
                    <button
                      onClick={() => setShowLeadSelector(campaign.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                    >
                      <UserPlus size={16} />
                      Import Leads
                    </button>
                  )}
                  {(campaign.status === 'draft' || campaign.status === 'paused') && (
                    <button
                      onClick={() => handleStart(campaign.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Start
                    </button>
                  )}
                  {campaign.status === 'active' && (
                    <button
                      onClick={() => handlePause(campaign.id)}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                    >
                      Pause
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Attribution View for Selected Campaign */}
            {selectedCampaignForAttribution === campaign.id && (
              <div className="mt-6 bg-white rounded-lg shadow p-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Attribution Analysis</h3>
                <AttributionVisualizer campaignId={campaign.id} />
              </div>
            )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Lead Selector Modal */}
      {showLeadSelector && (
        <LeadSelector
          campaignId={showLeadSelector}
          instantlyCampaignId={campaigns.find(c => c.id === showLeadSelector)?.instantly_campaign_id}
          onClose={() => setShowLeadSelector(null)}
          onImport={(count) => {
            fetchCampaigns();
            alert(`Successfully imported ${count} leads!`);
          }}
        />
      )}
    </div>
  );
}
